import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Send, Trash2, Pencil, X, Plus, Sparkles, Wrench, Bug, Bell, Users2, Check } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

// Developer-only composer for release notes / What's New announcements.
// Recipients are picked via checkboxes (empty selection = broadcast to
// everyone). Each recipient sees the pop-up exactly once — dismissals
// are tracked per-user id on the server so nobody gets bombarded.

const CATEGORIES = [
  { key: "new_feature", label: "New feature", icon: Sparkles, tone: "border-accent/40 text-accent" },
  { key: "improvement", label: "Improvement", icon: Wrench,   tone: "border-emerald-500/40 text-emerald-700 dark:text-emerald-300" },
  { key: "bug_fix",     label: "Bug fix",     icon: Bug,      tone: "border-amber-500/40 text-amber-700 dark:text-amber-300" },
  { key: "announcement",label: "Announcement",icon: Bell,     tone: "border-sky-500/40 text-sky-700 dark:text-sky-300" },
];

const EMPTY_FORM = { id: null, title: "", body: "", categories: ["new_feature"], target_user_ids: [] };

export default function WhatsNewPanel() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [notesR, usersR] = await Promise.all([
        api.get("/admin/whats-new"),
        api.get("/admin/users"),
      ]);
      setRows(notesR.data || []);
      setUsers(usersR.data || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not load announcements");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY_FORM); setEditorOpen(true); };
  const openEdit = (row) => {
    setForm({
      id: row.id,
      title: row.title,
      body: row.body || "",
      categories: row.categories?.length ? row.categories : ["new_feature"],
      target_user_ids: [...(row.target_user_ids || [])],
    });
    setEditorOpen(true);
  };

  const toggleCategory = (k) => setForm((f) => ({
    ...f,
    categories: f.categories.includes(k) ? f.categories.filter((c) => c !== k) : [...f.categories, k],
  }));

  const toggleUser = (uid) => setForm((f) => ({
    ...f,
    target_user_ids: f.target_user_ids.includes(uid)
      ? f.target_user_ids.filter((x) => x !== uid)
      : [...f.target_user_ids, uid],
  }));

  const selectAll = () => setForm((f) => ({ ...f, target_user_ids: users.map((u) => u.id) }));
  const selectNone = () => setForm((f) => ({ ...f, target_user_ids: [] }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = { title: form.title.trim(), body: form.body,
                         categories: form.categories, target_user_ids: form.target_user_ids };
      if (form.id) {
        await api.put(`/admin/whats-new/${form.id}`, payload);
        toast.success("Announcement updated");
      } else {
        await api.post("/admin/whats-new", payload);
        toast.success(form.target_user_ids.length === 0
          ? "Broadcast published to everyone"
          : `Published to ${form.target_user_ids.length} crew`);
      }
      setEditorOpen(false);
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    const t = deleteTarget; setDeleteTarget(null); if (!t) return;
    try {
      await api.delete(`/admin/whats-new/${t.id}`);
      toast.success("Announcement removed");
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Delete failed");
    }
  };

  const userNameById = useMemo(() => {
    const m = new Map();
    for (const u of users) m.set(u.id, u.name || u.email);
    return m;
  }, [users]);

  return (
    <div className="space-y-6" data-testid="whatsnew-panel">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-primary flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-accent" strokeWidth={1.4} /> What&apos;s New
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Compose short release notes and target them at specific crew members. Each recipient sees the popup exactly once — dismissals are tracked per user.
          </p>
        </div>
        <Button onClick={openNew} data-testid="whatsnew-new-btn" className="rounded-full">
          <Plus className="h-4 w-4 mr-2" /> New announcement
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border rounded-lg">
          No announcements yet — publish your first release note to introduce recent changes to the crew.
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => {
            const totalTargets = r.target_user_ids.length === 0 ? users.length : r.target_user_ids.length;
            const dismissedCount = r.dismissed_by.length;
            const pending = Math.max(0, totalTargets - dismissedCount);
            return (
              <li key={r.id} className="border border-border rounded-lg p-4 bg-card" data-testid={`whatsnew-row-${r.id}`}>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-medium text-primary">{r.title}</h3>
                      {(r.categories || []).map((c) => {
                        const meta = CATEGORIES.find((x) => x.key === c);
                        if (!meta) return null;
                        const Icon = meta.icon;
                        return (
                          <Badge key={c} variant="outline" className={`text-[9px] ${meta.tone}`}>
                            <Icon className="inline h-2.5 w-2.5 mr-1" />{meta.label}
                          </Badge>
                        );
                      })}
                    </div>
                    {r.body && (
                      <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{r.body}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-3 mt-3 text-[11px] text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Users2 className="h-3 w-3" />
                        {r.target_user_ids.length === 0
                          ? <><span className="text-foreground/80 font-medium">Everyone</span> ({users.length})</>
                          : <><span className="text-foreground/80 font-medium">{r.target_user_ids.length}</span> crew</>}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Check className="h-3 w-3 text-emerald-600" />
                        <span className="text-foreground/80 font-medium">{dismissedCount}</span> read
                      </span>
                      {pending > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                          <span className="text-accent font-medium">{pending}</span> pending
                        </span>
                      )}
                      {r.created_at && (
                        <span>· {new Date(r.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                      )}
                    </div>
                    {r.target_user_ids.length > 0 && (
                      <div className="text-[10px] text-muted-foreground/80 mt-1.5 truncate">
                        Sent to: {r.target_user_ids.map((uid) => userNameById.get(uid) || uid.slice(0, 6)).join(", ")}
                      </div>
                    )}
                  </div>
                  <div className="flex sm:flex-col gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => openEdit(r)} data-testid={`whatsnew-edit-${r.id}`} className="h-8">
                      <Pencil className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(r)} data-testid={`whatsnew-delete-${r.id}`} className="h-8 text-destructive hover:text-destructive">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Editor */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="whatsnew-editor">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {form.id ? "Edit announcement" : "New announcement"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs">Title *</Label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Version 1.4 — Regulars panel is live" data-testid="whatsnew-title" autoFocus />
            </div>

            <div>
              <Label className="text-xs mb-2 block">Category</Label>
              <div className="flex flex-wrap gap-2" data-testid="whatsnew-categories">
                {CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  const checked = form.categories.includes(c.key);
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => toggleCategory(c.key)}
                      data-testid={`whatsnew-category-${c.key}`}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs border transition-colors ${checked ? `${c.tone} bg-accent/5 font-medium` : "border-border text-muted-foreground hover:border-primary"}`}
                    >
                      <span className={`w-3 h-3 border ${checked ? "border-accent bg-accent" : "border-border"} flex items-center justify-center`}>
                        {checked && <Check className="h-2 w-2 text-white" strokeWidth={3} />}
                      </span>
                      <Icon className="h-3 w-3" /> {c.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs">Details (free text)</Label>
              <Textarea value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} rows={5} placeholder="What changed, why it matters, and how the crew should use it…" data-testid="whatsnew-body" />
            </div>

            {/* Target recipients */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Send to</Label>
                <div className="flex items-center gap-2 text-[11px]">
                  <button type="button" className="text-accent hover:underline" onClick={selectAll} data-testid="whatsnew-select-all">Select all</button>
                  <span className="text-muted-foreground/60">·</span>
                  <button type="button" className="text-muted-foreground hover:underline" onClick={selectNone} data-testid="whatsnew-select-none">Clear</button>
                </div>
              </div>
              <div className="border border-border rounded-lg divide-y divide-border max-h-64 overflow-y-auto" data-testid="whatsnew-recipients">
                {users.length === 0 ? (
                  <div className="text-xs text-muted-foreground p-4 text-center">No dashboard users yet.</div>
                ) : users.map((u) => {
                  const checked = form.target_user_ids.includes(u.id);
                  return (
                    <label key={u.id} className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-muted/50 ${checked ? "bg-accent/[0.04]" : ""}`} data-testid={`whatsnew-recipient-${u.id}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleUser(u.id)} className="accent-accent" />
                      <span className="flex-1 truncate">{u.name || u.email}</span>
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{u.role}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1.5">
                {form.target_user_ids.length === 0
                  ? "→ No recipients selected — will broadcast to every dashboard user."
                  : `→ ${form.target_user_ids.length} recipient${form.target_user_ids.length === 1 ? "" : "s"} selected.`}
              </p>
            </div>
          </div>

          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setEditorOpen(false)} data-testid="whatsnew-cancel">Cancel</Button>
            <Button onClick={submit} disabled={saving || !form.title.trim()} data-testid="whatsnew-save">
              {saving ? "Publishing…" : (form.id ? "Save changes" : <><Send className="h-3 w-3 mr-1" /> Publish</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="whatsnew-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove &ldquo;{deleteTarget?.title}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              Any crew who haven&apos;t seen it yet won&apos;t get the popup. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive hover:bg-destructive/90" data-testid="whatsnew-delete-confirm-btn">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
